/**
 * PLP utilities.
 *
 * Note: This file intentionally contains shared pure helpers used across PLP presentation/hooks.
 */

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeSidForComparison(value: string | undefined | null): string {
  const raw = value ?? '';
  let current = raw.toString().trim();
  if (!current) return '';

  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      break;
    }
  }
  return current.replace(/=+$/g, '');
}

function safeAtob(value: string): string {
  if (!value) return '';
  try {
    if (typeof globalThis.atob === 'function') return globalThis.atob(value);
    return Buffer.from(value, 'base64').toString('utf-8');
  } catch {
    return '';
  }
}

function safeDecodeURIComponent(value: string): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function decodeSidList(value: string | undefined | null): string[] {
  const raw = value?.trim() ?? '';
  if (!raw) return [];

  const decodedUri = safeDecodeURIComponent(raw);
  const base64Decoded = safeAtob(decodedUri);
  const source = base64Decoded || decodedUri;

  return source
    .split(',')
    .map((sid) => normalizeSidForComparison(sid))
    .filter(Boolean);
}

export function getSelectedStoreBySid<TStore extends { sid?: string | undefined | null }>(
  cityStoreMapUnknown: unknown,
  sids: string[]
): TStore | null {
  if (!isObject(cityStoreMapUnknown)) return null;
  const cityStoreMap = cityStoreMapUnknown as Record<string, unknown>;

  for (const cityName of Object.keys(cityStoreMap)) {
    const cityStoresUnknown = cityStoreMap[cityName];
    if (!Array.isArray(cityStoresUnknown)) continue;
    const cityStores = cityStoresUnknown as TStore[];
    const matched = cityStores.find((store) =>
      sids.includes(normalizeSidForComparison(store?.sid))
    );
    if (matched) return matched;
  }

  return null;
}

export function getSelectedStoresBySids<TStore extends { sid?: string | undefined | null }>(
  cityStoreMapUnknown: unknown,
  sids: string[]
): TStore[] {
  if (!isObject(cityStoreMapUnknown)) return [];
  if (!Array.isArray(sids) || sids.length === 0) return [];
  const cityStoreMap = cityStoreMapUnknown as Record<string, unknown>;
  const sidSet = new Set(sids);
  const selected: TStore[] = [];

  for (const cityName of Object.keys(cityStoreMap)) {
    const cityStoresUnknown = cityStoreMap[cityName];
    if (!Array.isArray(cityStoresUnknown)) continue;
    const cityStores = cityStoresUnknown as TStore[];
    for (const store of cityStores) {
      const normalized = normalizeSidForComparison(store?.sid);
      if (normalized && sidSet.has(normalized)) selected.push(store);
    }
  }

  return selected;
}

export function getGoogleMapDirectionsLink(destinationPlaceId: string): string {
  return `https://www.google.com/maps/search/?api=1&query=BlueStone+Jewellery&query_place_id=${encodeURIComponent(
    destinationPlaceId
  )}`;
}

export function normalizeBluestoneCanonical(raw: string): string {
  if (!raw) return 'https://www.bluestone.com/';
  try {
    const url = new URL(raw, 'https://www.bluestone.com');
    const path = url.pathname || '/';
    return `https://www.bluestone.com${path}`;
  } catch {
    return 'https://www.bluestone.com/';
  }
}

